import { motion } from 'framer-motion';
import { GlassCard } from '../components/ui/GlassCard';
import { Activity, Server, Zap, Cpu, Network } from 'lucide-react';

const stats = [
    { label: "NEURAL_SYNC", value: "99.99%", status: "OPTIMAL", icon: <Zap className="text-emerald-400" /> },
    { label: "AGENT_COGNITION", value: "142ms", status: "FAST", icon: <Activity className="text-sky-400" /> },
    { label: "CORE_TEMPERATURE", value: "32°C", status: "STABLE", icon: <Cpu className="text-orange-400" /> },
    { label: "UPLINK_STRENGTH", value: "1.2 Gbps", status: "ACTIVE", icon: <Network className="text-purple-400" /> }
];

const nodes = [
    { id: "NODE_ALPHA", load: "24%", memory: "4.2GB", status: "ONLINE" },
    { id: "NODE_BETA", load: "68%", memory: "12.8GB", status: "ONLINE" },
    { id: "NODE_GAMMA", load: "12%", memory: "1.1GB", status: "ONLINE" },
    { id: "NODE_DELTA", load: "0%", memory: "0GB", status: "STANDBY" }
];

export function Dashboard() {
    return (
        <div className="pt-32 px-6 md:px-12 max-w-7xl mx-auto min-h-screen pb-20">
            <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
                <div>
                    <h1 className="text-5xl font-black text-white tracking-tighter mb-4 uppercase">
                        AGENTIC <span className="text-emerald-500">OPERATIONS</span>
                    </h1>
                    <p className="text-gray-400 font-mono text-sm tracking-widest">
                        SYSEVAL_V2.0 // MISSION_CONTROL_INTERFACE
                    </p>
                </div>
                <div className="flex gap-4">
                    <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-md">
                        <span className="text-xs font-mono text-emerald-500">SYSTEM_TIME: {new Date().toLocaleTimeString()}</span>
                    </div>
                    <div className="px-4 py-2 bg-sky-500/10 border border-sky-500/20 rounded-md">
                        <span className="text-xs font-mono text-sky-500">ENCRYPTION: AES-256</span>
                    </div>
                </div>
            </div>

            {/* Quick Stats Bento */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
                {stats.map((stat, i) => (
                    <GlassCard key={i} className="p-6 border-white/5 hover:border-emerald-500/30 transition-all group">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-2 bg-white/5 rounded-lg group-hover:scale-110 transition-transform">{stat.icon}</div>
                            <span className="text-[10px] font-mono text-emerald-500/60">{stat.status}</span>
                        </div>
                        <div className="text-3xl font-black text-white mb-1">{stat.value}</div>
                        <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">{stat.label}</div>
                    </GlassCard>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Main Visualization Area */}
                <GlassCard className="md:col-span-2 min-h-[400px] border-emerald-500/10 bg-black/40 relative flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#10b98108_1px,transparent_1px),linear-gradient(to_bottom,#10b98108_1px,transparent_1px)] bg-[size:40px_40px]" />

                    <div className="relative z-10 text-center">
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                            className="w-48 h-48 border-2 border-emerald-500/20 rounded-full flex items-center justify-center"
                        >
                            <div className="w-40 h-40 border border-sky-500/20 rounded-full flex items-center justify-center animate-pulse">
                                <Activity className="w-12 h-12 text-emerald-400" />
                            </div>
                        </motion.div>
                        <p className="mt-8 font-mono text-xs text-emerald-500/60 animate-pulse">LIVESTREAM_INITIALIZED...</p>
                    </div>

                    {/* Corner accents */}
                    <div className="absolute top-4 left-4 w-4 h-4 border-t-2 border-l-2 border-emerald-500/40" />
                    <div className="absolute top-4 right-4 w-4 h-4 border-t-2 border-r-2 border-emerald-500/40" />
                    <div className="absolute bottom-4 left-4 w-4 h-4 border-b-2 border-l-2 border-emerald-500/40" />
                    <div className="absolute bottom-4 right-4 w-4 h-4 border-b-2 border-r-2 border-emerald-500/40" />
                </GlassCard>

                {/* Node Status Sidebar */}
                <div className="space-y-6">
                    <h3 className="text-xl font-bold text-white font-mono flex items-center gap-3">
                        <Server className="w-5 h-5 text-emerald-500" />
                        NODES_CLUSTER_01
                    </h3>
                    <div className="space-y-4">
                        {nodes.map((node, i) => (
                            <GlassCard key={i} className="p-4 border-white/5 flex items-center justify-between group hover:bg-white/5 transition-colors">
                                <div className="space-y-1">
                                    <div className="text-sm font-bold text-white">{node.id}</div>
                                    <div className="text-[10px] font-mono text-gray-500">M: {node.memory} // L: {node.load}</div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className={`w-2 h-2 rounded-full ${node.status === 'ONLINE' ? 'bg-emerald-500 shadow-[0_0_8px_#10b981]' : 'bg-gray-700'}`} />
                                    <span className="text-[10px] font-mono text-gray-400">{node.status}</span>
                                </div>
                            </GlassCard>
                        ))}
                    </div>

                    <button className="w-full py-4 bg-white/5 border border-white/10 rounded-lg text-xs font-mono text-gray-400 hover:bg-emerald-500 hover:text-black transition-all">
                        MANAGE_RESOURCES
                    </button>
                </div>
            </div>
        </div>
    );
}
